"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const client_library_1 = require("@seneca/client-library");
const BadBatchRequestError_1 = __importDefault(require("./errors/BadBatchRequestError"));
const ChildNotFoundError_1 = __importDefault(require("./errors/ChildNotFoundError"));
const ChildToParentLimitExceededError_1 = __importDefault(require("./errors/ChildToParentLimitExceededError"));
const CodeNotValidError_1 = __importDefault(require("./errors/CodeNotValidError"));
const ConnectionAlreadyAcceptedError_1 = __importDefault(require("./errors/ConnectionAlreadyAcceptedError"));
const ConnectionAlreadyRejectedError_1 = __importDefault(require("./errors/ConnectionAlreadyRejectedError"));
const EmailNotValidError_1 = __importDefault(require("./errors/EmailNotValidError"));
const EmailSentRecentlyError_1 = __importDefault(require("./errors/EmailSentRecentlyError"));
const FailedToDecryptConnectionKeyError_1 = __importDefault(require("./errors/FailedToDecryptConnectionKeyError"));
const NotifiedRecentlyError_1 = __importDefault(require("./errors/NotifiedRecentlyError"));
const ParentAlreadyExistsError_1 = __importDefault(require("./errors/ParentAlreadyExistsError"));
const ParentChildRelationshipAlreadyExistsError_1 = __importDefault(require("./errors/ParentChildRelationshipAlreadyExistsError"));
const ParentChildRelationshipNotFoundError_1 = __importDefault(require("./errors/ParentChildRelationshipNotFoundError"));
const ParentChildRelationshipRejectedError_1 = __importDefault(require("./errors/ParentChildRelationshipRejectedError"));
const ParentChildrenLimitExceededError_1 = __importDefault(require("./errors/ParentChildrenLimitExceededError"));
const ParentNotFoundByCodeError_1 = __importDefault(require("./errors/ParentNotFoundByCodeError"));
const ParentNotFoundByUserIdError_1 = __importDefault(require("./errors/ParentNotFoundByUserIdError"));
const ParentNotFoundError_1 = __importDefault(require("./errors/ParentNotFoundError"));
const UserNotAllowedToConnectToSelfError_1 = __importDefault(require("./errors/UserNotAllowedToConnectToSelfError"));
const formatStudyTimeDate_1 = __importDefault(require("./utils/formatStudyTimeDate"));
class ParentsServiceClient extends client_library_1.Client {
    constructor(fetch, options = {}) {
        super(fetch, options);
    }
    async fetchParent({ includePending = false } = {}) {
        const url = `${this.url}/api/parents/me?includePending=${includePending}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, [200, 404]);
        if (response.status === 404) {
            throw new ParentNotFoundError_1.default();
        }
        return response.json;
    }
    async parentHasChild(childUserId) {
        try {
            const parent = await this.fetchParent();
            return parent.children.some((child) => child.userId === childUserId);
        }
        catch (error) {
            if (error instanceof ParentNotFoundError_1.default) {
                return false;
            }
            throw error;
        }
    }
    async createParent() {
        const url = `${this.url}/api/parents/me`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST' }, [201, 409]);
        if (response.status === 409) {
            throw new ParentAlreadyExistsError_1.default();
        }
        return response.json;
    }
    async removeMyChild(childUserId) {
        const url = `${this.url}/api/parents/me/children/${childUserId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'DELETE' }, [204, 404]);
        if (response.status === 404) {
            throw new ChildNotFoundError_1.default(childUserId);
        }
    }
    async fetchChild({ includePending = false } = {}) {
        const url = `${this.url}/api/children/me?includePending=${includePending}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, [200]);
        return response.json;
    }
    async acceptConnection(childUserId) {
        const url = `${this.url}/api/connection/accept?childUserId=${childUserId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'PUT' }, [200]);
        return response.json;
    }
    async rejectConnection(childUserId) {
        const url = `${this.url}/api/connection/reject?childUserId=${childUserId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'PUT' }, [200]);
        return response.json;
    }
    async addMyParent(code) {
        var _a, _b;
        const url = `${this.url}/api/children/me`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify({ code }) }, [201, 404, 409, 422, 400]);
        if (response.status === 404) {
            throw new ParentNotFoundByCodeError_1.default(code);
        }
        if (response.status === 409) {
            throw new ParentChildRelationshipAlreadyExistsError_1.default();
        }
        if (response.status === 422) {
            throw new CodeNotValidError_1.default(code);
        }
        if (response.status === 400 &&
            ((_a = response === null || response === void 0 ? void 0 : response.json) === null || _a === void 0 ? void 0 : _a.pointer) === 'CHILDREN_LIMIT_EXCEEDED') {
            throw new ParentChildrenLimitExceededError_1.default();
        }
        if (response.status === 400 &&
            ((_b = response === null || response === void 0 ? void 0 : response.json) === null || _b === void 0 ? void 0 : _b.pointer) === 'NOT_ALLOWED_TO_CONNECT_TO_SELF') {
            throw new UserNotAllowedToConnectToSelfError_1.default();
        }
        return response.json;
    }
    async addMyParentByEmail(email, source) {
        var _a, _b, _c, _d;
        const url = `${this.url}/api/children/me/email`;
        const sanitisedEmail = email.toLocaleLowerCase();
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({ email: sanitisedEmail, source })
        }, [201, 404, 409, 422, 400]);
        if (response.status === 409 &&
            ((_a = response === null || response === void 0 ? void 0 : response.json) === null || _a === void 0 ? void 0 : _a.pointer) === 'CHILD_CONNECTION_REJECTED') {
            throw new ParentChildRelationshipRejectedError_1.default();
        }
        if (response.status === 409) {
            throw new ParentChildRelationshipAlreadyExistsError_1.default();
        }
        if (response.status === 422) {
            throw new EmailNotValidError_1.default(email);
        }
        if (response.status === 400 &&
            ((_b = response === null || response === void 0 ? void 0 : response.json) === null || _b === void 0 ? void 0 : _b.pointer) === 'CHILDREN_LIMIT_EXCEEDED') {
            throw new ParentChildrenLimitExceededError_1.default();
        }
        if (response.status === 400 &&
            ((_c = response === null || response === void 0 ? void 0 : response.json) === null || _c === void 0 ? void 0 : _c.pointer) === 'PARENTS_LIMIT_EXCEEDED') {
            throw new ChildToParentLimitExceededError_1.default();
        }
        if (response.status === 400 &&
            ((_d = response === null || response === void 0 ? void 0 : response.json) === null || _d === void 0 ? void 0 : _d.pointer) === 'NOT_ALLOWED_TO_CONNECT_TO_SELF') {
            throw new UserNotAllowedToConnectToSelfError_1.default();
        }
        return response.json;
    }
    async removeMyParent(parentUserId) {
        const url = `${this.url}/api/children/me/parents/${parentUserId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'DELETE' }, [204, 404]);
        if (response.status === 404) {
            throw new ParentNotFoundByUserIdError_1.default(parentUserId);
        }
    }
    async notifyParent(parentUserId, source) {
        var _a, _b, _c;
        const url = `${this.url}/api/children/me/parents/${parentUserId}/notify`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify({ source }) }, [200, 400, 404]);
        if (response.status === 404) {
            throw new ParentChildRelationshipNotFoundError_1.default();
        }
        if (response.status === 400) {
            if (((_a = response.json) === null || _a === void 0 ? void 0 : _a.pointer) === 'CONNECTION_ALREADY_ACCEPTED') {
                throw new ConnectionAlreadyAcceptedError_1.default();
            }
            if (((_b = response.json) === null || _b === void 0 ? void 0 : _b.pointer) === 'CONNECTION_ALREADY_REJECTED') {
                throw new ConnectionAlreadyRejectedError_1.default();
            }
            if (((_c = response.json) === null || _c === void 0 ? void 0 : _c.pointer) === 'NOTIFIED_RECENTLY') {
                throw new NotifiedRecentlyError_1.default();
            }
        }
    }
    async fetchChildConnections(userId) {
        const url = `${this.url}/api/admin/fetchChildConnections/${userId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, [200]);
        return response.json;
    }
    async resendParentInviteEmail(userId, parentUserId, inviterUserId) {
        var _a, _b, _c;
        const url = `${this.url}/api/admin/parents/remind`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({ userId, parentUserId, inviterUserId })
        }, [200, 400, 404]);
        if (response.status === 404) {
            throw new ParentChildRelationshipNotFoundError_1.default();
        }
        if (response.status === 400) {
            if (((_a = response.json) === null || _a === void 0 ? void 0 : _a.pointer) === 'CONNECTION_ALREADY_ACCEPTED') {
                throw new ConnectionAlreadyAcceptedError_1.default();
            }
            if (((_b = response.json) === null || _b === void 0 ? void 0 : _b.pointer) === 'CONNECTION_ALREADY_REJECTED') {
                throw new ConnectionAlreadyRejectedError_1.default();
            }
            if (((_c = response.json) === null || _c === void 0 ? void 0 : _c.pointer) === 'EMAIL_SENT_RECENTLY') {
                throw new EmailSentRecentlyError_1.default();
            }
        }
    }
    async batchResendParentInviteEmails(connectionKeys, inviterUserId) {
        var _a;
        const url = `${this.url}/api/admin/parents/remind/batch`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({ connectionKeys, inviterUserId })
        }, [200, 400]);
        if (response.status === 400) {
            if (((_a = response.json) === null || _a === void 0 ? void 0 : _a.pointer) === 'BAD_BATCH_REQUEST') {
                throw new BadBatchRequestError_1.default();
            }
        }
    }
    async batchDeleteChildConnections(connectionKeys) {
        const url = `${this.url}/api/admin/child-connections`;
        await this.requestMaker.makeRequest(url, {
            method: 'DELETE',
            body: JSON.stringify({ connectionKeys })
        }, [204]);
    }
    async batchCreateChildConnections(params) {
        const url = `${this.url}/api/admin/child-connections`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify(params)
        }, [200, 400]);
        if (response.status === 400) {
            throw new BadBatchRequestError_1.default();
        }
        return response.json;
    }
    async batchFetchParentConnections(userIds) {
        const url = `${this.url}/api/admin/child-connections/batch/fetch`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({ userIds })
        }, [200]);
        return response.json;
    }
    async fetchParentInformationByEncryptedConnectionKey(connectionKey) {
        const url = `${this.url}/api/parent-information/${connectionKey}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, [200, 400, 404]);
        if (response.status === 400) {
            throw new FailedToDecryptConnectionKeyError_1.default(connectionKey);
        }
        if (response.status === 404) {
            throw new ParentChildRelationshipNotFoundError_1.default();
        }
        return response.json;
    }
    async fetchChildrenSubscriptions() {
        const url = `${this.url}/api/parent/children/subscriptions`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'GET'
        }, [200]);
        return response.json;
    }
    async reassignSubscriptionToChild(args) {
        const { subscriptionId, reassignToUserId } = args;
        this._validateMissingParams({
            subscriptionId,
            reassignToUserId
        });
        const url = `${this.url}/api/parent/subscriptions/${subscriptionId}/reassign`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({ reassignToUserId })
        }, [200, 400, 403, 404, 422]);
        if (response.status !== 200) {
            throw new client_library_1.ExtendableError('Cannot reassign subscription');
        }
        return response.json;
    }
    async fetchChildWeeklyReport({ childUserId, week, year }) {
        this._validateMissingParams({
            childUserId,
            week,
            year
        });
        const url = `${this.url}/api/parents/me/children/${childUserId}/report?week=${week}&year=${year}`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'GET'
        }, [200, 400, 403]);
        if (response.status !== 200) {
            throw new client_library_1.ExtendableError('Cannot fetch weekly report');
        }
        return response.json;
    }
    async fetchChildStudyTime({ childUserId, period, from, to }) {
        this._validateMissingParams({
            childUserId,
            period,
            from,
            to
        });
        const formattedFrom = (0, formatStudyTimeDate_1.default)(from, period);
        const formattedTo = (0, formatStudyTimeDate_1.default)(to, period);
        const url = `${this.url}/api/parents/me/children/${childUserId}/study-time?period=${period}&from=${formattedFrom}&to=${formattedTo}`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'GET'
        }, [200, 400, 403]);
        if (response.status !== 200) {
            throw new client_library_1.ExtendableError('Cannot fetch study time');
        }
        return response.json;
    }
}
exports.default = ParentsServiceClient;
//# sourceMappingURL=ParentsServiceClient.js.map